/// <reference types="@types/google.accounts" />
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core'
import { FormControl, Validators, FormGroup } from '@angular/forms'
import {
    HttpClient,
    HttpErrorResponse,
    HttpResponse,
    HttpStatusCode,
} from '@angular/common/http'
import { Router } from '@angular/router'
import { CallerService } from '../services/caller.service'
import { GoogleSignInService } from '../services/google-signin.service'
import { setCorpMemberProfileData } from '../ngrx-store/actions/corp-member.actions'
import { environment } from 'src/environments/environment'
import { Subscription } from 'rxjs'

/**
 * README
 * 1. https://dribbble.com/shots/17408454-Login-Form-Prototype We should use the right side to show some important info
 * 1.1 Like new features, or just welcome them
 * 
 * @todo: make sure we're following the OWASP recommendations for security.
 * @todo: make sure we're following best practices from https://web.dev/articles/sign-in-form-best-practices.
 */

declare interface PostResponse {
    message: string
    response_code: number
    response: string
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('passwordInput') passwordInput!: ElementRef

    @ViewChild('googleSignInButton', { read: ElementRef<HTMLDivElement> })
    GoogleSignInButton!: ElementRef<HTMLDivElement>;

    isLoggingIn: boolean = false

    passwordInputIcon: string = 'visibility'

    private subscriptions: Subscription = new Subscription()

    constructor(
        private callerService: CallerService,
        private httpClient: HttpClient,
        private router: Router,
        private googleSignInService: GoogleSignInService,
    ) {}

    loginForm = new FormGroup({
        username: new FormControl('', [
            Validators.required,
            Validators.minLength(4),
        ]),
        password: new FormControl('', [
            Validators.required,
            Validators.minLength(6),
        ]),
    })

    ngOnInit() {
        // call the /hi endpoint to wake up the server
        this.callerService.sayHi().subscribe({
            error: (error) => {
                console.error('Failed to wake up the server:', error);
            }
        })
    }

    login() {
        if (this.loginForm.valid) {
            this.isLoggingIn = true

            this.callerService.corpMemberLogIn(this.loginForm.value).subscribe({
                next: (res: HttpResponse<any>) => {

                    if (res.status === HttpStatusCode.Ok) {
                        // TODO: Do we need to use localStorage for corper details??
                        sessionStorage.setItem(CallerService.SESSION_STORAGE_DATA_KEY, res.body.token);
                        localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(res.body.data))
                        // SocketEffects.connectSocketsOnProfileSet$ reacts to this dispatch and connects
                        // the sockets — see that effect for why this is centralized there now.
                        this.callerService.store.dispatch(setCorpMemberProfileData({data: res.body.data}))

                        if (res.body.data.email) {
                            const storeCredentialSub = this.googleSignInService.storeCredential({
                                id: res.body.data.email,
                                password: this.loginForm.get('password')?.value ?? '',
                            }, () => {
                                console.log('Credential stored successfully');
                            }).subscribe({
                                error: (error) => {
                                    console.log('error storing credential', error)
                                }
                            });
                            this.subscriptions.add(storeCredentialSub);
                        }

                        this.callerService.redirectAfterAuth()
                    } else {
                        // TODO: show error message if available
                        this.callerService.showNotification(
                            'Wrong username or password',
                            7000,
                            'Close',
                        )
                    }

                    this.isLoggingIn = false
                },
                error: (err) => {

                    this.isLoggingIn = false
                    this.callerService.showNotification(
                        err?.error?.message ?? 'Try that again please, an error occurred',
                        4000,
                        'Close',
                    )
                },
            })
        } else {
            // tell the user
            this.callerService.showNotification(
                'Invalid form input',
                4000,
                'Close',
            )
        }
    }

    ngAfterViewInit() {
        // Initialize Google Sign-In button after the view has been initialized
        this.initializeGoogleSignIn();
    }

    initializeGoogleSignIn() {
        if (!this.GoogleSignInButton?.nativeElement) {
            console.warn('Google Sign-In button element not found');
            return;
        }

        // Initialize Google Sign-In
        const initSub = this.googleSignInService.initializeGoogleSignIn({
            client_id: environment.GOOGLE_CLIENT_ID,
            auto_select: true,
            ...(Boolean(this.callerService.corpMember.email) && { login_hint: this.callerService.corpMember.email }),
            callback: this.handleCredentialResponse.bind(this),
        }).subscribe({
            next: () => {
                // After initialization, render the button
                const renderSub = this.googleSignInService.renderButton(
                    this.GoogleSignInButton.nativeElement,
                    { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', width: 256 }
                ).subscribe({
                    error: (error) => {
                        console.error('Failed to render Google Sign-In button:', error);
                        this.callerService.showNotification(
                            'Failed to load Google Sign-In. Please refresh the page.',
                            5000,
                            'Close'
                        );
                    }
                });
                this.subscriptions.add(renderSub);
            },
            error: (error) => {
                console.error('Failed to initialize Google Sign-In:', error);
                this.callerService.showNotification(
                    'Failed to load Google Sign-In. Please refresh the page.',
                    5000,
                    'Close'
                );
            }
        });
        this.subscriptions.add(initSub);

        // For one-tap sign-in prompt:
        // this.googleSignInService.loadGoogleSignInScript().subscribe(google => {
        //     google.accounts.id.prompt();
        // });
    }

    handleCredentialResponse(response: any) {
        this.callerService.corpMemberGoogleLogin({ google_token: response.credential }).subscribe({
            next: (res: HttpResponse<any>) => {
                sessionStorage.setItem(CallerService.SESSION_STORAGE_DATA_KEY, res.body.token);
                localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(res.body.data))
                // SocketEffects.connectSocketsOnProfileSet$ reacts to this dispatch and connects
                // the sockets — see that effect for why this is centralized there now.
                this.callerService.store.dispatch(setCorpMemberProfileData({data: res.body.data}))

                this.callerService.redirectAfterAuth()
            },
            error: (error) => {
                console.error('Failed to login with Google:', error);
                this.callerService.showNotification(
                    // 403: Google hasn't verified the email - trying again won't help, so say why.
                    (error?.status === 403 && error?.error?.message) || 'Failed to login with Google. Please try again.',
                    5000,
                    'Close'
                );
            }
        })
    }

    togglePasswordInputIcon(): void {
        if (this.passwordInputIcon == 'visibility') {
            this.passwordInputIcon = 'visibility_off'
            this.passwordInput.nativeElement.type = 'text'
        } else {
            this.passwordInputIcon = 'visibility'
            this.passwordInput.nativeElement.type = 'password'
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
